import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Checkbox from '@material-ui/core/Checkbox';
import Tooltip from '@material-ui/core/Tooltip';
import { lighten } from '@material-ui/core/styles/colorManipulator';
import Button from '@material-ui/core/Button';
import FormLabel from '@material-ui/core/FormLabel';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';

let counter = 0;

const columnData = [
    { id: 'field', numeric: false, disablePadding: false, label: 'Attribute/Field To Aggregate' },
    { id: 'type', numeric: false, disablePadding: false, label: 'Aggregation Type' }
];

class EnhancedTableHead extends React.Component {
    createSortHandler = property => event => {
        this.props.onRequestSort(event, property);
    };

    render() {
        const { onSelectAllClick, order, orderBy, numSelected, rowCount, editMode, records } = this.props;

        return (
            <TableHead>
                <TableRow>
                    <TableCell style={{ padding: 1 }}>
                        <Checkbox
                            indeterminate={numSelected > 0 && numSelected < rowCount}
                            checked={numSelected === rowCount}
                            onChange={onSelectAllClick}
                            disabled={editMode}
                        />
                    </TableCell>
                    {columnData.map(column => {
                        return (
                            <TableCell
                                key={column.id}
                                numeric={column.numeric}
                                padding={column.disablePadding ? 'none' : 'default'}
                                sortDirection={orderBy === column.id ? order : false}
                            >
                                <Tooltip
                                    title="Sort"
                                    placement={column.numeric ? 'bottom-end' : 'bottom-start'}
                                    enterDelay={300}
                                >
                                    <TableSortLabel
                                        active={orderBy === column.id}
                                        direction={order}
                                        onClick={this.createSortHandler(column.id)}
                                        style={{ fontSize: '11pt' }}>
                                        {column.label}
                                    </TableSortLabel>
                                </Tooltip>
                            </TableCell>
                        );
                    }, this)}
                </TableRow>
            </TableHead>
        );
    }
}

EnhancedTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.string.isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

const toolbarStyles = theme => ({
    root: {
        paddingRight: theme.spacing.unit,
    },
    highlight: theme.palette.type === 'light'
        ? {
            color: theme.palette.secondary.main,
            backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
        : {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.secondary.dark,
        },
    spacer: {
        flex: '1 1 100%',
    },
    actions: {
        color: theme.palette.text.secondary,
    },
    title: {
        flex: '0 0 auto',
    },
});

let EnhancedTableToolbar = props => {
    const { numSelected, classes } = props;

    return (
        <Toolbar
            className={classNames(classes.root, {
                [classes.highlight]: numSelected > 0,
            })}
        >
            <div className={classes.title}>
                {numSelected > 0 ? (
                    <Typography color="inherit" variant="subheading">
                        {numSelected} selected
                    </Typography>
                ) : (
                        <Typography variant="title" id="tableTitle">
                            Select All
                    </Typography>
                    )}
            </div>
        </Toolbar>
    );
};

EnhancedTableToolbar.propTypes = {
    classes: PropTypes.object.isRequired,
    numSelected: PropTypes.number.isRequired,
};

EnhancedTableToolbar = withStyles(toolbarStyles)(EnhancedTableToolbar);

const styles = theme => ({
    root: {
        width: '100%',
        marginTop: theme.spacing.unit * 3,
    },
    table: {
        minWidth: 100,
        width: "auto"
    },
    tableWrapper: {
        overflowX: 'auto',
        overflowY: 'hidden'
    },
    button: {
        'margin-right': theme.spacing.unit
    },
    buttonGroup: {
        display: 'flex',
        'flex-direction': 'row'
    },
    headers: {
        fontSize: 22
    }
});

class AggregatesTable extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            order: 'asc',
            orderBy: 'field',
            selected: [],
            data: [].sort((a, b) => (a.field < b.field ? -1 : 1)),
            aggregationTypes: [],
            fieldsToAggregate: [],
            aggregates: [],
            groupId: null,
            attributes: [],
            editMode: true,
            records: null,

        };
    }

    componentDidUpdate(prevProps) {
        if (isEmpty(prevProps.aggTypes) && !isEmpty(this.props.aggTypes)) {
            this.setState({ editMode: this.props.editDataGroupParams.editMode });
        }

        if (isEmpty(prevProps.attributes) && !isEmpty(this.props.attributes)) {
            //TODO move logic from render in here 
            //console.log('we should set attrs here ');
        }
    }

    handleRequestSort = (event, property) => {
        const orderBy = property;
        let order = 'desc';

        if (this.state.orderBy === property && this.state.order === 'desc') {
            order = 'asc';
        }

        const data =
            order === 'desc'
                ? this.state.data.sort((a, b) => (b[orderBy] < a[orderBy] ? -1 : 1))
                : this.state.data.sort((a, b) => (a[orderBy] < b[orderBy] ? -1 : 1));

        this.setState({ data, order, orderBy });
    };

    handleSelectAllClick = (event, checked) => {
        if (checked) {
            this.setState({ selected: this.state.data.map(n => n.id) });
            return;
        }
        this.setState({ selected: [] });
    };

    handleClick = (event, id) => {
        const { selected } = this.state;
        const selectedIndex = selected.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }

        this.setState({ selected: newSelected });
    };

    isSelected = id => this.state.selected.indexOf(id) !== -1;

    handleAddRow = () => {
        if (this.state.data.length === this.props.aggregationsLimit) {
            this.props.clientLimitExceeded("Aggregations");
        } else {
            counter += 1;
            const newRow = {
                id: counter,
                field: "",
                type: "",
                attribute: null,
                aggregationTypeId: null
            };
            this.setState({
                data: [...this.state.data, newRow]
            });
        }
        this.sendAggregates();
    };

    handleDeleteRow = () => {
        const toDelete = this.state.selected;

        for (let i = this.state.data.length - 1; i > -1; i--) {
            let obj = this.state.data[i];
            if (toDelete.indexOf(obj.id) != -1) {
                this.state.data.splice(i, 1);
            }
        }
        this.state.selected = [];
        this.sendAggregates();
        this.forceUpdate();
    };

    handleFieldChange = (event) => {
        for (let i = 0; i < this.state.data.length; i++) {
            if (this.state.data[i].id == event.target.id) {
                this.state.data[i].field = this.state.data[i].attribute = event.target.value;
                break;
            }
        }
        this.sendAggregates();
        this.forceUpdate();
    };

    handleTypeChange = (event) => {
        for (let i = 0; i < this.state.data.length; i++) {
            if (this.state.data[i].id == event.target.id) {
                this.state.data[i].type = event.target.value;
                this.state.data[i].aggregationTypeId = this.getAggregationTypeId(event.target.value);
                break;
            }
        }
        this.sendAggregates();
        this.forceUpdate();
    };

    getAggregationTypeId(aggregationType) {
        /*let type = this.aggregationTypes.find(obj => {
            return obj.name == aggregationType
        });
        return typeof type != "undefined" ? type.id : null;*/


        let aggTypeId = null;
        this.aggregationTypes.forEach(function (type) {
            if (type.name == aggregationType) {
                aggTypeId = type.id;
            }
        });
        return aggTypeId;
    }

    static setFieldsToAggregate(attributes) {
        let fieldsToAggregate = [];
        for (let att in attributes) {
            fieldsToAggregate.push({
                value: attributes[att],
                name: attributes[att]
            })
        }
        return fieldsToAggregate;
    }

    sendAggregates = () => {
        this.props.aggregatesCallback({
            attributesAndAggregationTypes: this.getAggregates(),
            unsavedChanges: this.checkForUnsavedChanges()
        });
    };

    checkForUnsavedChanges() {
        if (this.props.savedGroup != null && typeof this.props.savedGroup != 'undefined') {
            let first = this.props.savedGroup.attributesAndAggregationTypes.map(a => a.aggregationTypeId + '' + a.attribute).sort();
            let second = this.getAggregates().map(a => a.aggregationTypeId + '' + a.attribute).sort();
            return !(first.length === second.length && first.every((element, index) => isEqual(element, second[index])));
        }
    }

    getAggregates() {
        let aggregates = [];
        this.state.data.forEach(function (row) {
            if (row.attribute != null && row.attribute != '' && row.aggregationTypeId != null) {
                aggregates.push({
                    attribute: row.attribute,
                    aggregationTypeId: row.aggregationTypeId
                });
            }
        });
        return aggregates;
    }

    setTableData(params, aggregationTypes, fieldsToAggregate) {
        let data = [];
        let id = 1;
        let invalidAggregateFound = false;
        params.attributesAndAggregationTypes.forEach(function (record) {
            let attribute = fieldsToAggregate.find(obj => {
                return record.attribute == obj.value;
            });
            if (typeof attribute != 'undefined') {
                let type = aggregationTypes.find(obj => {
                    return obj.id === record.aggregationTypeId
                });
                typeof type != 'undefined' ?
                    data.push({
                        aggregationTypeId: record.aggregationTypeId,
                        attribute: record.attribute,
                        field: record.attribute,
                        id: id++,
                        type: typeof type != 'undefined' ? type.name : ''
                    }) : null
            } else {
                if (fieldsToAggregate.length > 0) {
                    invalidAggregateFound = true;
                }
            }
        });
        counter = id - 1;
        if (data.length > 0) {
            data.sort((a, b) => (a.field < b.field ? -1 : 1));
        }
        this.setState({ data }, () => {
            if (invalidAggregateFound) {
                this.sendAggregates();
            }
        });
    }

    render() {
        const { classes, aggTypes, attributes, aggregatesCallback, editDataGroupParams } = this.props;
        const { data, order, orderBy, selected, editMode, records } = this.state;
        this.aggregationTypes = aggTypes;
        this.fieldsToAggregate = AggregatesTable.setFieldsToAggregate(attributes);

        if (Object.keys(editDataGroupParams).length !== 0 && aggTypes.length > 0 &&
            (editDataGroupParams.id != this.state.groupId || attributes != this.state.attributes ||
                editDataGroupParams.records != this.state.records)) {
            this.setTableData(editDataGroupParams, aggTypes, this.fieldsToAggregate);
            this.state.groupId = editDataGroupParams.id;
            this.state.attributes = attributes;
            this.state.editMode = editDataGroupParams.editMode;
            this.state.records = editDataGroupParams.records;
            this.forceUpdate();
        }

        return (
            <div className={classes.root}>
                <div className="row buttonGroup">
                    <div className="col-lg-7">
                        <FormLabel className={classes.headers}>
                            Define Aggregates and Types of Aggregation
                        </FormLabel>
                    </div>
                    <div className="float-right">
                        <Button variant="outlined" color="secondary" className={classes.button} onClick={this.handleAddRow}
                            disabled={editMode}>
                            Add
                        </Button>
                        <Button variant="outlined" color="secondary" className={classes.button}
                            disabled={this.state.selected.length < 1} onClick={this.handleDeleteRow}>
                            Delete
                        </Button>
                    </div>
                </div>
                <br />
                <Paper>
                    {/*<EnhancedTableToolbar numSelected={selected.length}/>*/}
                    <div className={classes.tableWrapper}>
                        <Table className={classes.table} aria-labelledby="tableTitle">
                            <EnhancedTableHead
                                numSelected={selected.length}
                                order={order}
                                orderBy={orderBy}
                                onSelectAllClick={this.handleSelectAllClick}
                                onRequestSort={this.handleRequestSort}
                                rowCount={data.length}
                                editMode={editMode}
                                records={records}
                            />
                            <TableBody>
                                {data.map(n => {
                                    const isSelected = this.isSelected(n.id);
                                    return (
                                        <TableRow
                                            hover={records == null || records == 0}
                                            aria-checked={isSelected}
                                            tabIndex={-1}
                                            key={n.id}
                                            selected={isSelected}
                                        >
                                            <TableCell style={{ padding: 1 }}>
                                                <Checkbox onClick={event => this.handleClick(event, n.id)}
                                                    disabled={editMode}
                                                    checked={isSelected} />
                                            </TableCell>
                                            <TableCell style={{ width: '50%', padding: 10 }}>
                                                <select
                                                    id={n.id}
                                                    onChange={this.handleFieldChange}
                                                    value={n.field}
                                                    style={{ border: '1px solid gray', width: "100%", borderRadius: 5, fontSize: 17 }}
                                                    disabled={editMode}
                                                >
                                                    <option key={0} value={''}>{''}</option>
                                                    {this.fieldsToAggregate.map((e, key) => {
                                                        return <option key={e.value} value={e.value}>{e.name}</option>;
                                                    })}
                                                </select>
                                            </TableCell>
                                            <TableCell style={{ width: '50%', padding: 10 }} >
                                                <select
                                                    id={n.id}
                                                    onChange={this.handleTypeChange}
                                                    value={n.type}
                                                    style={{ border: '1px solid gray', width: "100%", borderRadius: 5, fontSize: 17 }}
                                                    disabled={editMode}
                                                >
                                                    <option key={0} value={''}>{''}</option>
                                                    {this.aggregationTypes.map((e, key) => {
                                                        return <option key={e.id} value={e.name}>{e.name}</option>;
                                                    })}
                                                </select>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                </Paper>

            </div>
        );
    }
}

AggregatesTable.propTypes = {
    classes: PropTypes.object.isRequired,
    editDataGroupParams: PropTypes.object,
    savedGroup: PropTypes.object,
    attributes: PropTypes.array,
    aggregatesCallback: PropTypes.func,
    clientLimitExceeded: PropTypes.func
};

export default withStyles(styles)(AggregatesTable);



// WEBPACK FOOTER //
// ./src/main/content/define-data-group/AggregatesTable.js